import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import pandoraAdminService, { PipelineStats } from '../../../services/pandoraAdminService';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  uploaded: 'default',
  ingested: 'info',
  classified: 'info',
  segmented: 'warning',
  validated: 'success',
  promoted: 'success',
  rejected: 'error',
  error: 'error',
};

const CATEGORY_LABELS: Record<string, string> = {
  petition: 'Petição',
  law: 'Legislação',
  legal_case: 'Jurisprudência',
  legal_theory: 'Doutrina',
  verbetes: 'Verbetes',
};

function getStatusColor(status: string): 'default' | 'info' | 'warning' | 'success' | 'error' {
  const key = status.toLowerCase();
  return STATUS_COLORS[key] ?? 'default';
}

function getCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

export default function PandoraDashboardPage() {
  const [stats, setStats] = useState<PipelineStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await pandoraAdminService.getStats();
        setStats(data);
      } catch (err) {
        console.error('Erro ao carregar estatísticas Pandora:', err);
        setError('Não foi possível carregar as estatísticas do pipeline.');
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const s = stats ?? {
    total_documents: 0,
    total_paragraphs: 0,
    validated_paragraphs: 0,
    taxonomy_elements: 0,
    by_status: {},
    by_category: {},
    by_domain: {},
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Pandora - Validação de Textos Jurídicos
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Estatísticas do pipeline de documentos e taxonomia.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="overline">
                Total Documentos
              </Typography>
              <Typography variant="h4">{s.total_documents}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="overline">
                Parágrafos Validados
              </Typography>
              <Typography variant="h4">
                {s.validated_paragraphs} / {s.total_paragraphs}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="overline">
                Elementos Taxonomia
              </Typography>
              <Typography variant="h4">{s.taxonomy_elements}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Status do Pipeline
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.entries(s.by_status).map(([status, count]) => (
            <Chip
              key={status}
              label={`${status}: ${count}`}
              color={getStatusColor(status)}
              variant="outlined"
              size="medium"
            />
          ))}
          {Object.keys(s.by_status).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nenhum documento no pipeline.
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Por Categoria
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.entries(s.by_category).map(([category, count]) => (
            <Chip
              key={category}
              label={`${getCategoryLabel(category)}: ${count}`}
              variant="outlined"
              size="medium"
            />
          ))}
          {Object.keys(s.by_category).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nenhuma categoria com documentos.
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Por Área do Direito
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.entries(s.by_domain).map(([domain, count]) => (
            <Chip
              key={domain}
              label={`${domain}: ${count}`}
              variant="outlined"
              size="medium"
            />
          ))}
          {Object.keys(s.by_domain).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nenhuma área classificada.
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          onClick={() => navigate('/admin/pandora/upload')}
        >
          Upload Documento
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin/pandora/validation')}
        >
          Fila de Validação
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin/pandora/taxonomy')}
        >
          Taxonomia
        </Button>
      </Box>
    </Box>
  );
}
